import { useEffect, useState } from "react";
import {
  StructurePresetResponseDto,
  getStructurePresetsByCafeId,
  isDefaultPreset,
  removeTournamentPreset,
} from "../../../../api/tournament-preset";
import { useTournamentContext } from "../../hook/context";
import { EditTournamentTab } from "../../hook/useTournamentTab";
import LiveSetting from "./components/live-setting";
import {
  EditTournamentButtonWrapper,
  EditTournamentContentWrapper,
  EditTournamentDropBoxItem,
  EditTournamentDropBoxItemWrapper,
  EditTournamentDropBoxWrapper,
  EditTournamentHeaderButtonWrapper,
  EditTournamentHeaderWrapper,
  EditTournamentWrapper,
} from "./style";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { pubState } from "../../../../recoil/pub.atom";
import { dialogState } from "../../../../recoil/dialog.atom";
import { getTournamentTypeName } from "../../../../utils/tournament-type";
import EditTournamentTabs from "./components/tab";
import EditTournamentBase from "./components/base";
import EditTournamentPrize from "./components/prize";
import EditTournamentParticipation from "./components/participation";
import { DialogOverlay } from "../../../../component/InputDialog/style";
import InputDialog from "../../../../component/InputDialog";
import EditTournamentStructure from "./components/structure";
import { formatDateKor } from "../../../../utils/date";
import { EditTournamentReservation } from "./components/reservation";
import MobileHeader from "../../../../layout/mobile-header";

export interface EditTournamentPopupProps {
  onClose: () => void;
  tournamentId: number | null;
  getData?: any | null;
}

const MobileTournamentEdit = ({
  onClose,
  tournamentId,
  getData,
}: EditTournamentPopupProps) => {
  const pub = useRecoilValue(pubState);
  const {
    isTournamentTab,
    setSelectedPresetId,
    getDataByPreset,
    selectedPresetId,
    startDate,
    resetData,
    getDataById,
    registerStructure,
    updateStructure,
    registerStructurePreset,
    updateStructurePreset,
  } = useTournamentContext();
  const setDialog = useSetRecoilState(dialogState);
  const [isDropOpen, setIsDropOpen] = useState(false);
  const [presetList, setPresetList] = useState<StructurePresetResponseDto[]>(
    [],
  );
  const [presetTitle, setPresetTitle] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 프리셋 정보 ===============================================================
  // 프리셋 데이터
  const getPreset = async () => {
    if (!pub) return;
    const res = await getStructurePresetsByCafeId(pub.id, 1, 100);
    setPresetList(res.preset);
  };

  useEffect(() => {
    getPreset();
  }, [pub]);

  // 프리셋 삭제
  const handleDeletePreset = (id: number) => {
    if (!pub) return;
    setDialog({
      title: "삭제하시겠습니까?",
      confirm: async () => {
        await removeTournamentPreset(id, pub.id);
        getPreset();
      },
    });
  };

  // 프리셋 토너먼트 타입
  const handlePresetType = (item: StructurePresetResponseDto) => {
    const preset = JSON.parse(item.preset);
    return getTournamentTypeName(preset.tournamentType);
  };

  // 프리셋 생성
  const registerPreset = async () => {
    if (presetTitle.length < 3) {
      alert("최소 3자 입력해주세요.");
      return;
    }

    try {
      await registerStructurePreset(presetTitle);
      await getPreset();

      setIsDialogOpen(false);
    } catch (e) {
      setIsDialogOpen(false);
    }
  };

  // 프리셋 수정
  const updatePreset = async () => {
    if (!selectedPresetId) {
      return;
    }

    try {
      const result = await updateStructurePreset(selectedPresetId);
      if (result) {
        await getPreset();
        setSelectedPresetId(null);
        alert("프리셋이 수정되었습니다. ");
      }
    } catch (e) {}
  };

  // 프리셋 선택
  const handleSelectPreset = (preset: StructurePresetResponseDto) => {
    if (selectedPresetId === preset.id) {
      setSelectedPresetId(null);
      resetData();
    } else {
      setSelectedPresetId(preset.id);
      getDataByPreset(preset.preset);
    }
  };

  // 토너먼트 정보 =============================================================
  useEffect(() => {
    tournamentId && getDataById(tournamentId);
  }, [tournamentId]);

  // 토너먼트 정보 업데이트
  const handleUpdateTournament = async () => {
    if (!tournamentId) return;

    try {
      const result = await updateStructure(tournamentId);
      if (!result) return;
      if (result?.code === 200) {
        onClose();
      } else {
        alert(result.message);
      }
    } catch (e: any) {
      alert(e.message);
      console.error(e);
    } finally {
      if (getData) {
        getData();
      }
    }
  };

  // 토너먼트 정보 생성
  const handleCreateTournament = async () => {
    if (!startDate) {
      alert("시작일을 선택해 주세요.");
      return;
    }

    setDialog({
      title: `토너먼트 시작일이 ${formatDateKor(
        startDate,
      )}입니다.\n 게시하시겠습니까?`,
      confirm: async () => {
        try {
          const result = await registerStructure();
          if (!result) return;
          if (result?.code === 200) {
            onClose();
          } else {
            alert(result?.message);
          }
        } catch (e: any) {
          alert(e.message);
          console.error(e);
        } finally {
          if (getData) {
            getData();
          }
        }
      },
    });
  };

  return (
    <EditTournamentWrapper onClick={() => setIsDropOpen(false)}>
      <MobileHeader
        title={tournamentId ? "토너먼트 수정" : "토너먼트 생성"}
        back={onClose}
      />
      {isDialogOpen && <DialogOverlay />}
      {isDialogOpen && (
        <InputDialog
          title="프리셋 생성"
          content={presetTitle}
          setContent={setPresetTitle}
          setIsOpen={setIsDialogOpen}
          confirm={registerPreset}
        />
      )}
      <EditTournamentHeaderWrapper>
        <EditTournamentHeaderButtonWrapper>
          <div className="button" onClick={resetData}>
            새로 만들기
          </div>
          {!tournamentId && (
            <EditTournamentDropBoxWrapper
              onClick={(e) => {
                e.stopPropagation();
                setIsDropOpen(!isDropOpen);
              }}
            >
              <div className="name">
                {presetList.find((item) => item.id === selectedPresetId)
                  ?.presetName ?? "프리셋 불러오기"}
              </div>
              <img src="/image-web/tournament/drop-down.svg" alt="drop-down" />
              {isDropOpen && (
                <EditTournamentDropBoxItemWrapper>
                  {presetList.map((item, index) => (
                    <EditTournamentDropBoxItem
                      key={index}
                      selected={item.id === selectedPresetId}
                      onClick={() => handleSelectPreset(item)}
                    >
                      ({handlePresetType(item)}) {item.presetName}
                      {!isDefaultPreset(item.id) && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePreset(item.id);
                          }}
                        >
                          <path
                            d="M6 18L18 6M6 6L18 18"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </EditTournamentDropBoxItem>
                  ))}
                </EditTournamentDropBoxItemWrapper>
              )}
            </EditTournamentDropBoxWrapper>
          )}
        </EditTournamentHeaderButtonWrapper>
        <EditTournamentTabs />
      </EditTournamentHeaderWrapper>

      <EditTournamentContentWrapper>
        {isTournamentTab(EditTournamentTab.BASE) && <EditTournamentBase />}
        {isTournamentTab(EditTournamentTab.PRIZE) && <EditTournamentPrize />}
        {isTournamentTab(EditTournamentTab.PARTICIPATION) && (
          <EditTournamentParticipation />
        )}
        {isTournamentTab(EditTournamentTab.STRUCTURE) && (
          <EditTournamentStructure />
        )}
        {isTournamentTab(EditTournamentTab.LIVE) && <LiveSetting />}
        {isTournamentTab(EditTournamentTab.RESERVATION) && (
          <EditTournamentReservation />
        )}
      </EditTournamentContentWrapper>

      <EditTournamentButtonWrapper>
        <div
          className="button"
          onClick={() => {
            if (selectedPresetId && !isDefaultPreset(selectedPresetId)) {
              updatePreset();
            } else {
              setIsDialogOpen(true);
              setPresetTitle("");
            }
          }}
        >
          프리셋{" "}
          {selectedPresetId && !isDefaultPreset(selectedPresetId)
            ? "수정하기"
            : "저장하기"}
        </div>
        <div
          className="button purple"
          onClick={() =>
            tournamentId ? handleUpdateTournament() : handleCreateTournament()
          }
        >
          토너먼트 {tournamentId ? "수정하기" : "게시하기"}
        </div>
      </EditTournamentButtonWrapper>
    </EditTournamentWrapper>
  );
};

export default MobileTournamentEdit;
